import { useEffect, useRef } from 'react';
import type { StripeEmbeddedCheckout } from '@stripe/stripe-js';
import { _t } from '@/i18n/t';
import { getStripe } from '../subscriptionShared';

interface StripeCheckoutModalProps {
  clientSecret: string;
  /** Called once the embedded checkout completes, or the user dismisses it. */
  onFinish: () => void;
}

const StripeCheckoutModal = ({ clientSecret, onFinish }: StripeCheckoutModalProps) => {
  const checkoutRef = useRef<StripeEmbeddedCheckout | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Keeps the mount effect from capturing a stale callback.
  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;

  useEffect(() => {
    let cancelled = false;

    getStripe()
      ?.then((stripeInstance) => {
        if (cancelled) return;

        return stripeInstance!
          .createEmbeddedCheckoutPage({
            clientSecret,
            onComplete: () => onFinishRef.current(),
          })
          .then((checkout) => {
            if (cancelled) {
              checkout.destroy();
              return;
            }
            checkoutRef.current = checkout;
            if (containerRef.current) {
              checkout.mount(containerRef.current);
            }
          });
      })
      .catch((err: unknown) => {
        console.error('Failed to mount Stripe embedded checkout:', err);
      });

    return () => {
      cancelled = true;
      if (checkoutRef.current) {
        checkoutRef.current.destroy();
      }
    };
  }, [clientSecret]);

  return (
    <div
      data-testid="settings-stripe-checkout-backdrop"
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50"
      onClick={() => onFinishRef.current()}
    >
      <div
        className="bg-white rounded-xl w-[600px] max-w-[95vw] max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 pt-5">
          <h3 className="text-lg font-semibold m-0">
            {_t('settings.subscription.stripeModal.title')}
          </h3>
          <button
            className="bg-transparent border-none text-2xl cursor-pointer text-muted-foreground hover:text-foreground leading-none p-0"
            aria-label={_t('settings.subscription.stripeModal.close')}
            onClick={() => onFinishRef.current()}
          >
            &times;
          </button>
        </div>
        <div className="px-6 py-5 min-h-[400px]">
          <div ref={containerRef} />
        </div>
      </div>
    </div>
  );
};

export default StripeCheckoutModal;